import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getTransformedLogicTableFilterValue, mapOrderToSort } from '../utils';

/**
 * Sets table current active filters based on the table search params
 * @param tablePagination setters and stateful table pagination values and functions to update it
 * @param tableFiltersState table filters stateful value, and a function to update it
 * @param staticSearch static table search
 */
export const useTableFiltersSearchParams = (
  tablePagination,
  tableFiltersState,
  staticSearch = false,
) => {
  const [
    page,
    setPage,
    limit,
    setLimit,
    currentSort,
    setCurrentSort,
    currentSortType,
    setCurrentSortType,
    defaultSortType = '',
    setSearchText,
    debouncedSearchTerm,
    setDebouncedValue,
  ] = tablePagination;
  const [tableFilters, setTableFilters] = tableFiltersState;
  const { search } = useLocation();
  const { products } = useSelector((state) => state.products);
  const { userIsAdmin: hasOrganizationAccess } = useSelector(
    (state) => state.authentication,
  );

  // Updates filters
  const updateFilter = (
    filterName,
    filterParam,
    paramValue = null,
    paramSign = null,
  ) => {
    const isFilterExists = tableFilters?.hasOwnProperty(filterName);
    setTableFilters((current) => {
      if (!isFilterExists && !filterParam) return current;

      if (isFilterExists && !filterParam) {
        const { [filterName]: currFilter, ...filters } = current;
        return filters;
      }

      return {
        ...current,
        [filterName]: {
          value: paramValue ?? filterParam,
          ...(paramSign && { sign: paramSign }),
          ...(paramSign && { touched: true }),
        },
      };
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    const paramPage = Number(params.get('page'));
    const paramLimit = Number(params.get('limit')) || 10;
    const paramSearch = params.get('searchQuery') || '';
    const paramOrder = params.get('order');
    const currentOrder = `${currentSortType} ${mapOrderToSort(currentSort)}`;
    const paramSortType = paramOrder?.split(' ')?.[0];
    const paramDirection = paramOrder?.split(' ')?.[1];
    const paramType = params.get('type');
    const filterType = tableFilters?.type?.value?.toLowerCase();
    const paramTreatmentType = params.get('treatmentType');
    const filterTreatmentType =
      tableFilters?.treatment_type?.value?.toLowerCase();
    const paramCompleted = params.get('completed');
    const filterCompleted = tableFilters?.completed?.value;
    const paramStartDate = params
      .get('startDate')
      ?.split(',')
      ?.map((date) => date);
    const filterStratDate = tableFilters?.start_date?.value;
    const paramDecrease = params.get('decrease')?.split(',')?.map(Number);
    const filterDecrease = tableFilters?.decrease?.value;
    const paramStartScore = params.get('startScore')?.split(',')?.map(Number);
    const filterStartScore = tableFilters?.start_score?.value;
    const paramEndScore = params.get('endScore')?.split(',')?.map(Number);
    const filterEndScore = tableFilters?.end_score?.value;
    const paramDeviceName = params.get('deviceName');
    const filterDeviceName = tableFilters?.device_name?.value;
    const paramOrgId = params.get('orgId');
    const filterOrgId = tableFilters?.organizationId?.value;
    const paramShowDeleted = params.get('showDeleted');
    const filterShowDeleted = tableFilters?.show_deleted?.value;
    const paramSerialNumber = params.get('serialNumber');
    const filterSerialNumber = tableFilters?.generated_uuid?.value;
    const paramDeviceType = params.get('deviceType');
    const filterDeviceType = tableFilters?.device_type?.value;

    const paramTotalSessions = params.get('totalSessions');
    const paramTotalSessionsSign = Object.keys(
      JSON.parse(paramTotalSessions) || {},
    )?.[0];
    const paramTotalSessionsValue =
      JSON.parse(paramTotalSessions)?.[paramTotalSessionsSign];

    const paramMonthlySessions = params.get('monthlySession');
    const paramMonthlySessionsSign = Object.keys(
      JSON.parse(paramMonthlySessions) || {},
    )?.[0];
    const paramMonthlySessionsValue =
      JSON.parse(paramMonthlySessions)?.[paramMonthlySessionsSign];

    const paramTherapists = params.get('therapists');
    const paramTherapistsSign = Object.keys(
      JSON.parse(paramTherapists) || {},
    )?.[0];
    const paramTherapistsValue =
      JSON.parse(paramTherapists)?.[paramTherapistsSign];

    const paramActiveClients = params.get('activeClients');
    const paramActiveClientsSign = Object.keys(
      JSON.parse(paramActiveClients) || {},
    )?.[0];
    const paramActiveClientsValue =
      JSON.parse(paramActiveClients)?.[paramActiveClientsSign];

    // Page changed
    if (page * limit !== paramPage) setPage(Math.floor(paramPage / paramLimit));

    // Page limit changed
    if (limit !== paramLimit) setLimit(paramLimit === 0 ? 10 : paramLimit);

    // Search changed
    const paramSearchNotEqualToFilter =
      JSON.stringify(paramSearch) !== JSON.stringify(debouncedSearchTerm) &&
      !staticSearch;
    if (paramSearchNotEqualToFilter)
      setDebouncedValue((prev) => {
        if (paramSearch === '') {
          setSearchText('');
          return '';
        }
        setSearchText(paramSearch);
        return paramSearch;
      });

    // Sort changed
    if (paramOrder != currentOrder) {
      setCurrentSort(mapOrderToSort(paramDirection) || currentSort);
      setCurrentSortType(paramSortType || defaultSortType);
    }

    // Product type changed
    if (paramType != filterType) updateFilter('type', paramType);

    // Device type changed
    if (paramDeviceType != filterDeviceType)
      updateFilter('device_type', paramDeviceType);

    // Treatment product type changed
    if (paramTreatmentType != filterTreatmentType)
      updateFilter('treatment_type', paramTreatmentType);

    // Device name changed
    if (paramDeviceName != filterDeviceName)
      updateFilter('device_name', paramDeviceName);

    // Organization Id changed
    if (paramOrgId != filterOrgId && hasOrganizationAccess)
      updateFilter('organizationId', paramOrgId);

    // Completed status changed
    if (paramCompleted != filterCompleted)
      updateFilter('completed', paramCompleted);

    // Show deleted filter changed
    if (Boolean(paramShowDeleted) != Boolean(filterShowDeleted))
      updateFilter('show_deleted', paramShowDeleted);

    // Serial Number filter changed
    if (paramSerialNumber != filterSerialNumber)
      updateFilter('generated_uuid', paramSerialNumber);

    // decrease filter changed
    const paramDecreaseNotEqualToFilter =
      JSON.stringify(paramDecrease) !== JSON.stringify(filterDecrease);
    if (paramDecreaseNotEqualToFilter) updateFilter('decrease', paramDecrease);

    // StartScore filter changed
    const paramStartScoreNotEqualToFilter =
      JSON.stringify(paramStartScore) !== JSON.stringify(filterStartScore);
    if (paramStartScoreNotEqualToFilter)
      updateFilter('start_score', paramStartScore);

    // EndScore filter changed
    const paramEndDateNotEqualToFilter =
      JSON.stringify(paramEndScore) !== JSON.stringify(filterEndScore);
    if (paramEndDateNotEqualToFilter) updateFilter('end_score', paramEndScore);

    // startDate filter changed
    const paramStartDateNotEqualToFilter =
      JSON.stringify(paramStartDate) !== JSON.stringify(filterStratDate);
    if (paramStartDateNotEqualToFilter)
      updateFilter('start_date', paramStartDate);

    // Total Sessions filter changed
    const totalSessionsChanged =
      getTransformedLogicTableFilterValue(tableFilters, 'sessions') !=
      paramTotalSessions;

    if (totalSessionsChanged)
      updateFilter(
        'sessions',
        paramTotalSessions,
        paramTotalSessionsValue,
        paramTotalSessionsSign,
      );

    // Monthly Sessions filter changed
    const monthlySessionsChanged =
      getTransformedLogicTableFilterValue(tableFilters, 'monthly_sessions') !=
      paramMonthlySessions;

    if (monthlySessionsChanged)
      updateFilter(
        'monthly_sessions',
        paramMonthlySessions,
        paramMonthlySessionsValue,
        paramMonthlySessionsSign,
      );

    // Therapists filter changed
    const therapistsChanged =
      getTransformedLogicTableFilterValue(tableFilters, 'therapists') !=
      paramTherapists;

    if (therapistsChanged)
      updateFilter(
        'therapists',
        paramTherapists,
        paramTherapistsValue,
        paramTherapistsSign,
      );

    // Active clients filter changed
    const activeClientsChanged =
      getTransformedLogicTableFilterValue(tableFilters, 'clients') !=
      paramActiveClients;

    if (activeClientsChanged)
      updateFilter(
        'clients',
        paramActiveClients,
        paramActiveClientsValue,
        paramActiveClientsSign,
      );
  }, [search]);
};
